import { useSnackbar } from '@app/hooks/useSnackbar';
import { Button, NotificationModal } from '@components/ui';
import { useUserUpdateDpoMutation } from '@dieterApi/user/useUserUpdateDpoMutation';
import { useUserUploadDpoAvatarMutation } from '@dieterApi/user/useUserUploadDpoAvatarMutation';
import { useUserDeleteDpoAvatarMutation } from '@dieterApi/user/useUserDeleteDpoAvatarMutation';
import { ModalProps, TextField, Avatar, IconButton } from '@mui/material';
import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Upload as UploadIcon } from 'lucide-react';

interface Props extends Omit<ModalProps, 'children'> {
  userId: string;
  initialValues: {
    dpoName?: string | null;
    dpoEmail?: string | null;
    dpoPhone?: string | null;
    dpoAddress?: string | null;
    dpoCompany?: string | null;
    dpoAvatarUrl?: string | null;
  };
}

export function UserManagementDpoModal({ userId, initialValues, ...props }: Props) {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [updateUserDpo, { loading }] = useUserUpdateDpoMutation();
  const [uploadDpoAvatar, { loading: uploading }] = useUserUploadDpoAvatarMutation();
  const [deleteDpoAvatar, { loading: deleting }] = useUserDeleteDpoAvatarMutation();

  const [dpoName, setDpoName] = useState(initialValues.dpoName || '');
  const [dpoEmail, setDpoEmail] = useState(initialValues.dpoEmail || '');
  const [dpoPhone, setDpoPhone] = useState(initialValues.dpoPhone || '');
  const [dpoAddress, setDpoAddress] = useState(initialValues.dpoAddress || '');
  const [dpoCompany, setDpoCompany] = useState(initialValues.dpoCompany || '');
  const [avatarUrl, setAvatarUrl] = useState(initialValues.dpoAvatarUrl || null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Cleanup preview URL on unmount
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Check file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        enqueueSnackbar(t('route.user-management.dpo-modal.file-too-large', 'Datei zu groß. Maximal 5MB erlaubt.'), {
          variant: 'error',
        });
        return;
      }

      // Clean up old preview URL
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }

      // Create preview URL for the selected file
      const newPreviewUrl = URL.createObjectURL(file);
      setPreviewUrl(newPreviewUrl);
      setSelectedFile(file);
    }
  };

  const handleUploadAvatar = () => {
    if (!selectedFile) return;

    uploadDpoAvatar({
      variables: {
        file: selectedFile,
        size: selectedFile.size,
        userId,
      },
    })
      .then((result) => {
        setAvatarUrl(result.data?.uploadDpoAvatar.dpoAvatarUrl || null);
        setSelectedFile(null);

        // Clean up preview URL
        if (previewUrl) {
          URL.revokeObjectURL(previewUrl);
          setPreviewUrl(null);
        }

        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        enqueueSnackbar(t('route.user-management.dpo-modal.avatar-uploaded', 'Avatar hochgeladen'), {
          variant: 'success',
        });
      })
      .catch((e) => {
        enqueueSnackbar(e.message, { variant: 'error', stack: e.stack });
      });
  };

  const handleDeleteAvatar = () => {
    deleteDpoAvatar({
      variables: { userId },
    })
      .then(() => {
        setAvatarUrl(null);
        enqueueSnackbar(t('route.user-management.dpo-modal.avatar-deleted', 'Avatar gelöscht'), {
          variant: 'success',
        });
      })
      .catch((e) => {
        enqueueSnackbar(e.message, { variant: 'error', stack: e.stack });
      });
  };

  const handleSubmit = () => {
    updateUserDpo({
      variables: {
        userId,
        dpoName: dpoName || null,
        dpoEmail: dpoEmail || null,
        dpoPhone: dpoPhone || null,
        dpoAddress: dpoAddress || null,
        dpoCompany: dpoCompany || null,
      },
    })
      .then(() => {
        enqueueSnackbar(t('route.user-management.dpo-update-success', 'DSB-Daten aktualisiert'), {
          variant: 'success',
        });
        props.onClose?.({}, 'backdropClick');
      })
      .catch((e) => {
        enqueueSnackbar(e.message, { variant: 'error', stack: e.stack });
      });
  };

  const handleCancelSelection = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const getAvatarUrl = () => {
    // Show preview if file is selected
    if (previewUrl) return previewUrl;
    // Show uploaded avatar if exists
    if (avatarUrl) return avatarUrl;
    // Fallback to generated avatar
    const name = dpoName || initialValues.dpoName || dpoEmail || initialValues.dpoEmail || 'DSB';
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&size=128&background=a95ecd&color=fff`;
  };

  return (
    <NotificationModal {...props}>
      <div className="flex flex-col gap-5 p-3 md:p-12 min-w-[500px]">
        <div className="h3">{t('route.user-management.dpo-modal.header', 'DSB-Kontaktdaten bearbeiten')}</div>

        <p className="text-sm text-gray-600">
          {t(
            'route.user-management.dpo-modal.description',
            'Diese Kontaktdaten werden verwendet, wenn dieser Benutzer als Datenschutzbeauftragter für einen Mandanten zugewiesen wird.'
          )}
        </p>

        {/* Avatar section */}
        <div className="flex flex-col items-center gap-3 p-4 bg-gray-50 rounded-lg">
          <div className="relative">
            <Avatar src={getAvatarUrl()} sx={{ width: 96, height: 96 }} />
            {avatarUrl && !previewUrl && (
              <IconButton
                size="small"
                onClick={handleDeleteAvatar}
                disabled={deleting}
                sx={{
                  position: 'absolute',
                  top: -8,
                  right: -8,
                  bgcolor: 'error.main',
                  color: 'white',
                  '&:hover': { bgcolor: 'error.dark' },
                }}
              >
                <X size={16} />
              </IconButton>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />
          <div className="flex gap-2">
            {!selectedFile && (
              <Button
                btnColor="gray"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading || deleting}
              >
                <UploadIcon size={16} className="mr-1" />
                {t('route.user-management.dpo-modal.select-image', 'Bild auswählen')}
              </Button>
            )}
            {selectedFile && (
              <>
                <Button
                  onClick={handleUploadAvatar}
                  disabled={uploading || deleting}
                  spinner={uploading}
                >
                  {t('route.user-management.dpo-modal.upload', 'Hochladen')}
                </Button>
                <Button
                  btnColor="gray"
                  onClick={handleCancelSelection}
                  disabled={uploading || deleting}
                >
                  {t('common.cancel', 'Abbrechen')}
                </Button>
              </>
            )}
          </div>
          {selectedFile && (
            <p className="text-xs text-gray-600">{selectedFile.name}</p>
          )}
        </div>

        <div className="flex flex-col gap-4">
          <TextField
            label={t('route.user-management.dpo-modal.name', 'Name des Datenschutzbeauftragten')}
            variant="filled"
            fullWidth
            value={dpoName}
            onChange={(e) => setDpoName(e.target.value)}
          />

          <TextField
            label={t('route.user-management.dpo-modal.company', 'Firma')}
            variant="filled"
            fullWidth
            value={dpoCompany}
            onChange={(e) => setDpoCompany(e.target.value)}
          />

          <TextField
            label={t('route.user-management.dpo-modal.email', 'E-Mail')}
            variant="filled"
            fullWidth
            type="email"
            value={dpoEmail}
            onChange={(e) => setDpoEmail(e.target.value)}
          />

          <TextField
            label={t('route.user-management.dpo-modal.phone', 'Telefon')}
            variant="filled"
            fullWidth
            value={dpoPhone}
            onChange={(e) => setDpoPhone(e.target.value)}
          />

          <TextField
            label={t('route.user-management.dpo-modal.address', 'Adresse')}
            variant="filled"
            fullWidth
            multiline
            rows={3}
            value={dpoAddress}
            onChange={(e) => setDpoAddress(e.target.value)}
          />
        </div>

        <div className="flex gap-3">
          <Button onClick={handleSubmit} disabled={loading} spinner={loading}>
            {t('common.save', 'Speichern')}
          </Button>
          <Button btnColor="gray" onClick={() => props.onClose?.({}, 'backdropClick')}>
            {t('common.cancel', 'Abbrechen')}
          </Button>
        </div>
      </div>
    </NotificationModal>
  );
}
